import { useLazyQuery } from "@apollo/client";
import { clientCentralApiStrawberry } from "@neonumy/api-clients";
import { notification, validateId } from "@neonumy/neonumy-ui";
import { routes } from "config/routes";
import useStore from "config/store";
import { GET_ACCOUNTING_FIRM, GET_COMPANY } from "queries/centralapi";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

export default function useCompanyInitializer() {
  const { companyID, accountingFirmID } = useParams();
  const { t } = useTranslation();
  const userData = useStore(state => state.userData);
  const companySelected = useStore(state => state.companySelected);
  const accountingFirmSelected = useStore(state => state.accountingFirmSelected);
  const setCompanySelected = useStore(state => state.setCompanySelected);
  const setAccountingFirmSelected = useStore(state => state.setAccountingFirmSelected);
  const setCompanyData = useStore(state => state.setCompanyData);

  const [companyToFetch, setCompanyToFetch] = useState<ID>();

  const [getCompany, { loading: companyLoading }] = useLazyQuery<
    GetCompanyResponseType,
    GetCompanyInputType
  >(GET_COMPANY, { client: clientCentralApiStrawberry });

  const [getAccountingFirm, { loading: accountingFirmLoading }] = useLazyQuery<
    GetAccountingFirmResponseType,
    GetAccountingFirmInputType
  >(GET_ACCOUNTING_FIRM, { client: clientCentralApiStrawberry });

  const fetchCompany = async (id: ID) => {
    const { data, error } = await getCompany({ variables: { id } });

    if (error) {
      routes.navigate("/error", { replace: true });
      return;
    }

    if (data?.getCompany) {
      const { accountingFirm, ...company } = data.getCompany;
      setCompanyData(data.getCompany);
      setCompanySelected({
        id: company.id,
        name: company.name,
        internalReference: company.internalReference,
      });
      if (accountingFirm) {
        setAccountingFirmSelected({ id: accountingFirm.id, name: accountingFirm.name });
      }
      if (!companyID) {
        routes.navigate(`/company/${data.getCompany.id}`, { replace: true });
      }
    } else {
      routes.navigate("/", { replace: true });
      notification.error({ message: t("Company not found") });
    }
  };

  const fetchAccountingFirm = async (id: ID) => {
    const { data, error } = await getAccountingFirm({ variables: { accountingFirmId: id } });

    if (error) {
      routes.navigate("/error", { replace: true });
      return;
    }

    if (data?.getAccountingFirm) {
      const { id: accountingFirmId, name, companies } = data.getAccountingFirm;
      setCompanyData(undefined);
      setAccountingFirmSelected({ id: accountingFirmId, name });

      if (companies?.length === 1) {
        const [company] = companies;
        setCompanySelected(company);
        routes.navigate(`/company/${company.id}`, { replace: true });
      } else {
        setCompanySelected(undefined);
      }
    } else {
      routes.navigate("/", { replace: true });
      notification.error({ message: t("Accounting firm not found") });
    }
  };

  useEffect(() => {
    if (!userData) return;

    const { companies, accountingFirms } = userData;

    const companyId = (companies?.length === 1 && companies[0]) || companyID;
    const accountingFirmId =
      (accountingFirms?.length === 1 && accountingFirms[0]) || accountingFirmID;

    const selectedCompanyId = companySelected?.id;
    const selectedAccountingFirmId = accountingFirmSelected?.id;

    const shouldFetchCompany =
      companyId && validateId(companyId) && companyId !== selectedCompanyId;
    const shouldFetchAccountingFirm =
      accountingFirmId &&
      validateId(accountingFirmId) &&
      accountingFirmId !== selectedAccountingFirmId;

    if (shouldFetchCompany) {
      setCompanyToFetch(companyId);
    } else if (shouldFetchAccountingFirm) {
      fetchAccountingFirm(accountingFirmId);
    }

    if (companyID && !companyId) {
      routes.navigate("/", { replace: true });
    } else if (accountingFirmID && !accountingFirmId) {
      routes.navigate("/", { replace: true });
    }
  }, [userData, companyID, accountingFirmID]);

  useEffect(() => {
    if (companyToFetch && validateId(companyToFetch)) {
      fetchCompany(companyToFetch);
    }
  }, [companyToFetch]);

  return { loading: companyLoading || accountingFirmLoading };
}
